import { useEffect, useState, useRef } from "react"
import { auth, db } from "../firebase"
import { doc, getDoc, updateDoc } from "firebase/firestore"
import { useNavigate } from "react-router-dom"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { LuChevronLeft } from "react-icons/lu"
import "./Feed.css"

function Feed() {
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [feedContent, setFeedContent] = useState("")
  const [fetching, setFetching] = useState(true)

  const navigate = useNavigate()
  const lastTickRef = useRef(null)
  const bufferedSecondsRef = useRef(0)
  const allowExitRef = useRef(false)

  useEffect(() => {
    const init = async () => {
      const currentUser = auth.currentUser

      if (!currentUser) {
        navigate("/login", { replace: true })
        return
      }

      const snap = await getDoc(doc(db, "users", currentUser.uid))

      if (!snap.exists()) {
        navigate("/dashboard", { replace: true })
        return
      }

      setUser(currentUser)
      setUserData(snap.data())
      setLoading(false)
      lastTickRef.current = Date.now()
    }

    init()
  }, [navigate])

  useEffect(() => {
  if (!userData) return

  const formatKey = value =>
    value
      ?.toLowerCase()
      .replace(/computer science & engineering/i, "cse")
      .replace(/information technology/i, "it")
      .replace(/first year/i, "firstyear")
      .replace(/second year/i, "secondyear")
      .replace(/third year/i, "thirdyear")
      .replace(/final year/i, "finalyear")
      .replace(/software developer/i, "software")
      .replace(/ai \/ ml engineer/i, "aiml")
      .replace(/not sure yet/i, "software")
      .replace(/[^a-z0-9]+/g, "")
      .trim()

  const branchKey = formatKey(userData.branch)
  const yearKey = formatKey(userData.year)

  const goalKey =
    userData.careerGoal === "Not Sure Yet"
      ? "software"
      : formatKey(userData.careerGoal)

  const skillKey =
    userData.careerGoal === "Not Sure Yet"
      ? "beginner"
      : formatKey(userData.skillLevel)

  const fetchFeed = async () => {
    setFetching(true)
    try {
      const response = await fetch(
        `https://raw.githubusercontent.com/sanketpadhyal/sluz-feed/main/feed/${branchKey}/${yearKey}/${skillKey}/${goalKey}`
      )

      if (!response.ok) throw new Error("Not found")

      const text = await response.text()
      setFeedContent(text)
    } catch {
      setFeedContent("# Content Not Available\nPlease check back later.")
    }
    setFetching(false)
  }

  fetchFeed()
}, [userData])

  useEffect(() => {
    if (!user) return

    const userRef = doc(db, "users", user.uid)

    const saveToFirestore = async () => {
      if (!lastTickRef.current) return

      const now = Date.now()
      const diffSeconds = Math.floor((now - lastTickRef.current) / 1000)
      if (diffSeconds <= 0) return

      bufferedSecondsRef.current += diffSeconds
      lastTickRef.current = now

      if (bufferedSecondsRef.current < 30) return

      const minutesToSave = bufferedSecondsRef.current / 60
      bufferedSecondsRef.current = 0
      const todayKey = new Date().toLocaleDateString("en-CA")

      const snap = await getDoc(userRef)
      if (!snap.exists()) return

      const data = snap.data()
      const screen = data.screentime || {}

      const updatedScreen = {
        ...screen,
        [todayKey]: parseFloat(
          ((screen[todayKey] || 0) + minutesToSave).toFixed(2)
        )
      }

      const updatedTotal = parseFloat(
        ((data.totalScreenTime || 0) + minutesToSave).toFixed(2)
      )

      await updateDoc(userRef, {
        screentime: updatedScreen,
        totalScreenTime: updatedTotal
      })

      // 🔥 REMOVED setUserData here to stop scroll jump
    }

    const forceFlush = async () => {
      if (bufferedSecondsRef.current <= 0) return

      const minutesToSave = bufferedSecondsRef.current / 60
      bufferedSecondsRef.current = 0
      const todayKey = new Date().toLocaleDateString("en-CA")

      const snap = await getDoc(userRef)
      if (!snap.exists()) return

      const data = snap.data()
      const screen = data.screentime || {}

      const updatedScreen = {
        ...screen,
        [todayKey]: parseFloat(
          ((screen[todayKey] || 0) + minutesToSave).toFixed(2)
        )
      }

      const updatedTotal = parseFloat(
        ((data.totalScreenTime || 0) + minutesToSave).toFixed(2)
      )

      await updateDoc(userRef, {
        screentime: updatedScreen,
        totalScreenTime: updatedTotal
      })
    }

    const interval = setInterval(saveToFirestore, 30000)

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        forceFlush()
      } else {
        lastTickRef.current = Date.now()
      }
    }

    const handleBeforeUnload = e => {
      if (!allowExitRef.current) {
        e.preventDefault()
        e.returnValue = ""
      }
      forceFlush()
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [user])

  const handleBack = () => {
    allowExitRef.current = true
    navigate("/dashboard")
  }

  if (loading || !userData) return null

  return (
    <div className="feed-page">
      <button className="back-btn-fixed" onClick={handleBack}>
        <LuChevronLeft />
      </button>

      <div className="feed-wrapper">
        <div className="feed-meta">
          <h1>Personalized Learning Blog</h1>

          {userData.branch?.toLowerCase().includes("computer science") && (
            <p className="branch-info">
              <strong>CSE</strong> stands for <strong>Computer Science and Engineering</strong>.
            </p>
          )}

          <div className="feed-tags">
            <span>{userData.branch}</span>
            <span>{userData.year}</span>
            <span>{userData.skillLevel}</span>
            <span>{userData.careerGoal}</span>
          </div>
        </div>

        <div className="feed-content">
          {fetching ? (
            <div className="feed-skeleton">
              <div className="skeleton-title"></div>
              <div className="skeleton-text"></div>
              <div className="skeleton-text short"></div>
              <div className="skeleton-subtitle"></div>
              <div className="skeleton-text"></div>
              <div className="skeleton-text"></div>
              <div className="skeleton-list">
                <div className="skeleton-item"></div>
                <div className="skeleton-item"></div>
                <div className="skeleton-item"></div>
                <div className="skeleton-item"></div>
              </div>
              <div className="skeleton-text"></div>
              <div className="skeleton-code"></div>
              <div className="skeleton-text"></div>
            </div>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {feedContent}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  )
}

export default Feed